/* Keep Shopify's contact endpoint and CAPTCHA; replace only the final transport. */
(() => {
  if (window.HbnContact) return;
  const instances = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  const mount = scope => {
    const roots = [...scope.querySelectorAll('[data-hbn-contact]')];
    if (scope.matches?.('[data-hbn-contact]')) roots.unshift(scope);
    roots.forEach(root => {
      if (instances.has(root)) return;
      const form = root.querySelector('form');
      const confirmation = root.querySelector('[data-contact-confirmation]');
      if (!form || !confirmation || confirmation.hasAttribute('data-hbn-contact-confirmed')) return;
      // A protection provider that has already captured native submit must retain it.
      // Normally this deferred asset mounts before the first interaction / CAPTCHA binding.
      if (form.dataset.hcaptchaBound || form.dataset.recaptchaBound) return;
      const nativeSubmit = form.submit;
      if (typeof nativeSubmit !== 'function') return;
      const feedback = root.querySelector('[data-contact-feedback]');
      const verification = root.querySelector('[data-contact-verification]');
      const fields = root.querySelector('.contact-form__fields');
      const actions = root.querySelector('.contact-form__actions');
      const buttons = [...form.querySelectorAll('[type="submit"]')];
      const events = new AbortController();
      let busy = false, posting = false, nativeMode = false, complete = false;
      let timer, request, attempt = 0;
      const notice = (message, focus = false) => {
        feedback.textContent = message;
        feedback.hidden = !message;
        if (message && focus) feedback.focus({ preventScroll: true });
      };
      const setBusy = value => {
        busy = value;
        form.setAttribute('aria-busy', String(value));
        buttons.forEach(button => { button.disabled = value; });
      };
      const finish = () => {
        clearTimeout(timer);
        posting = false;
        setBusy(false);
      };
      const showConfirmation = () => {
        complete = true;
        notice('');
        verification.hidden = true;
        fields.hidden = true;
        actions.hidden = true;
        confirmation.hidden = false;
        confirmation.setAttribute('data-hbn-contact-confirmed', '');
        confirmation.focus({ preventScroll: true });
        if (!reduced.matches) confirmation.animate?.(
          [{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }],
          { duration: 420, easing: 'ease-out' }
        );
      };
      // Shopify.captcha.protect wraps form.submit and invokes this transport only
      // after its verification has completed. Never call hCaptcha directly or drop tokens.
      const submitVerified = async () => {
        if (nativeMode) { nativeSubmit.call(form); return; }
        if (!busy || posting || complete || !root.isConnected) return;
        posting = true;
        clearTimeout(timer);
        request = new AbortController();
        timer = setTimeout(() => request.abort(), 30000);
        try {
          const action = new URL(form.action, window.location.href);
          if (action.origin !== window.location.origin) throw new Error('Unexpected form origin');
          const response = await fetch(action.href, {
            method: 'POST', body: new FormData(form), credentials: 'same-origin',
            headers: { Accept: 'text/html' }, redirect: 'follow', signal: request.signal
          });
          const url = new URL(response.url || action.href);
          if (url.origin !== window.location.origin) throw new Error('Unexpected response origin');
          if (/(^|\/)challenge(?:\/|$)/.test(url.pathname)) {
            notice(root.dataset.contactChallenge, true);
            verification.hidden = false;
            actions.hidden = true;
            return;
          }
          const documentResult = new DOMParser().parseFromString(await response.text(), 'text/html');
          const returnedForm = documentResult.getElementById(form.id);
          const serverError = returnedForm?.querySelector('[data-contact-feedback]');
          if (response.ok && returnedForm?.querySelector('[data-hbn-contact-confirmed]')) {
            showConfirmation();
          } else {
            // HTTP 200 alone is not proof that Shopify accepted the message.
            notice(serverError?.textContent.trim() || root.dataset.contactError, true);
          }
        } catch (_error) {
          if (root.isConnected) notice(root.dataset.contactError, true);
        } finally { finish(); }
      };
      form.submit = submitVerified;
      form.addEventListener('submit', event => {
        if (nativeMode) return; // Let Shopify persist fields and run its normal challenge flow.
        event.preventDefault();
        event.stopImmediatePropagation();
        if (busy || complete || !form.reportValidity()) return;
        setBusy(true);
        notice(root.dataset.contactPending);
        verification.hidden = true;
        const current = ++attempt;
        timer = setTimeout(() => {
          if (posting || !busy) return;
          ++attempt;
          finish();
          notice(root.dataset.contactVerifyError, true);
        }, 90000);
        const send = () => {
          if (!busy || current !== attempt) return;
          if (window.Shopify?.captcha?.protect && form.submit === submitVerified) {
            finish();
            notice(root.dataset.contactVerifyError, true);
            return;
          }
          form.submit();
        };
        if (window.Shopify?.captcha?.protect) {
          window.Shopify.captcha.protect(form, send);
        } else send(); // Stores without CAPTCHA still validate the POST on Shopify's server.
      }, { capture: true, signal: events.signal });
      verification.addEventListener('click', () => {
        if (busy || complete) return;
        nativeMode = true;
        form.requestSubmit();
      }, { signal: events.signal });
      instances.set(root, { destroy() {
        ++attempt; clearTimeout(timer); request?.abort(); events.abort();
        // Do not undo a wrapper that Shopify has subsequently installed.
        if (form.submit === submitVerified) form.submit = nativeSubmit;
      } });
    });
  };
  window.HbnContact = { mount };
  // Mount immediately for deferred assets, before Shopify's DOMContentLoaded binding.
  mount(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => {
      if (root === event.target || event.target.contains(root)) { instance.destroy(); instances.delete(root); }
    });
  });
})();
